"use client"

import React, { useState, useEffect, startTransition } from "react"

import { z } from "zod"
import { getQueryClient } from "@repo/core/src/lib/query-client"
import {
  CompanyUpdate,
  CompanyUpdatePayload,
  CompanyUpdateStatus,
  EditCompanyUpdatePayload,
  TProductAssetMetadata,
} from "@repo/utils"
import {
  useBusinessByUuidQuery,
  useCreateCompanyUpdateMutation,
  useEditCompanyUpdateMutation,
  useUserBusinessesQuery,
} from "@repo/core/src/services/businesses"
import { deleteFile, uploadFile } from "@repo/core/src/lib/s3"
import { useForm } from "../../hooks"
import { Loading } from "../common"
import ModalBasic from "../modal-basic"
import Banner02 from "../banner-02"
import { CustomSingleSelectInput, TextAreaInput, TextInput } from "../form"
import { ActionButton, ButtonLevelled } from "../buttons"
import { SingleImageUpload } from "../images"
import { FileWithPreview } from "../images/types"

const companyUpdatesSchema = z.object({
  name: z.string().min(1, { message: "Please add an update name." }),
  tag: z.string().optional(),
  category: z.string().optional(),
  message: z.string().optional(),
  status: z
    .enum(Object.values(CompanyUpdateStatus) as [CompanyUpdateStatus, ...CompanyUpdateStatus[]], {
      message: "Invalid status.",
    })
    .optional(),
})

interface CompanyUpdateModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  companyUpdate?: CompanyUpdate
  isEdit?: boolean
}

const CompanyUpdateModal = ({ isOpen, setIsOpen, companyUpdate, isEdit = false }: CompanyUpdateModalProps) => {
  const queryClient = getQueryClient()
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [successMessage, setSuccessMessage] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAwsLoading, setIsAwsLoading] = useState(false)
  const [_, setPreview] = useState<string | ArrayBuffer | null>("")
  const [status, setStatus] = useState<CompanyUpdateStatus>(companyUpdate?.status ?? "DRAFT")
  const [mediaFile, setMediaFile] = useState<FileWithPreview<TProductAssetMetadata> | null>(null)

  const { data: userWithBusinesses, isLoading: isLoadingUserWithBusinesses } = useUserBusinessesQuery()
  const selectedBusiness = userWithBusinesses?.selectedBusiness
  const { data: businessData, isLoading: isLoadingBusinessData } = useBusinessByUuidQuery(selectedBusiness?.uuid)

  const businessUuid = businessData?.uuid

  const { mutate } = useCreateCompanyUpdateMutation(businessUuid ?? "UUID_MISSING")
  const { mutate: editMutate } = useEditCompanyUpdateMutation(businessUuid ?? "UUID_MISSING")

  const companyUpdateImageKey = companyUpdate?.image?.key

  const companyUpdateStatusArray = Object.values(CompanyUpdateStatus)
  const statusOptions: { label: CompanyUpdateStatus; value: CompanyUpdateStatus }[] = companyUpdateStatusArray.map(status => ({
    label: status,
    value: status,
  }))

  const initialValues: { name: string; tag: string; category: string; message: string; image?: File | null; status?: CompanyUpdateStatus } = {
    name: companyUpdate?.name ?? "",
    tag: companyUpdate?.tag ?? "",
    category: companyUpdate?.category ?? "",
    message: companyUpdate?.message ?? "",
    ...(!isEdit && { image: null }),
    ...(companyUpdate?.status && { status: companyUpdate.status }),
  }

  const { values, errors, handleChange, formAction, isPending, message, resetForm } = useForm({
    initialValues,
    schema: companyUpdatesSchema,
    onSubmit: async formValues => {
      setIsLoading(true)

      const payload: CompanyUpdatePayload = {
        name: formValues.name,
        ...((formValues.tag && { tag: formValues.tag }) || (companyUpdate?.tag && !formValues.tag && { tag: "" })),
        ...((formValues.category && { category: formValues.category }) || (companyUpdate?.category && !formValues.tag && { category: "" })),
        message: formValues.message,
        status: formValues.status
      }

      if (!isEdit && !mediaFile) {
        setErrorMessage("Image is required")
        setIsLoading(false)
        return { success: false, error: true, values: formValues }
      }

      let fileUrl = null
      if (mediaFile && mediaFile.name.length > 0 && businessData?.uuid) {
        const body = mediaFile
        const { name } = body
        const filename = name
        const key = `${process.env.NEXT_PUBLIC_AWS_ENVIRONMENT}/${businessData.uuid}/company-update/${filename}`
        setIsAwsLoading(true)

        const arrayBuffer = await body.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)

        fileUrl = await uploadFile(key, uint8Array).finally(() => {
          setIsAwsLoading(false)
        })

        const imageKey = companyUpdate?.image?.key ?? null

        if (fileUrl != null && imageKey != null) {
          await deleteFile(imageKey)
        }
      }

      if (fileUrl) {
        payload.image = fileUrl
      }

      if (isEdit && companyUpdate) {
        let editPayload: EditCompanyUpdatePayload = { ...payload, updateId: companyUpdate.id }
        if (status !== companyUpdate.status) {
          editPayload.status = status
        }

        editMutate(editPayload, {
          onSuccess: () => {
            setSuccessMessage(`Successfully Updated!`)
            setErrorMessage("")
            setIsLoading(false)
            resetForm()
            setPreview(null)
            setIsOpen(false)
            queryClient.invalidateQueries({ queryKey: ["company-updates", businessUuid] })
            return { success: true, error: false }
          },
          onError: (err: any) => {
            console.error("Mutation error:", err)
            setIsLoading(false)
            setSuccessMessage("")
            const errMsg: string = Array.isArray(err.response?.data?.message)
              ? err.response.data.message.join(", ")
              : err.response?.data?.message || "An error occurred."
            setErrorMessage(errMsg)
            return { success: false, error: true, values: formValues }
          },
        })
      } else {
        mutate(payload, {
          onSuccess: () => {
            setSuccessMessage(`Company update created!`)
            setErrorMessage("")
            setIsLoading(false)
            resetForm()
            setPreview(null)
            setIsOpen(false)
            queryClient.invalidateQueries({ queryKey: ["company-updates", businessUuid] })
            return { success: true, error: false }
          },
          onError: (err: any) => {
            console.error("Mutation error:", err)
            setIsLoading(false)
            setSuccessMessage("")
            const errMsg: string = Array.isArray(err.response?.data?.message)
              ? err.response.data.message.join(", ")
              : err.response?.data?.message || "An error occurred."
            setErrorMessage(errMsg)
            return { success: false, error: true, values: formValues }
          },
        })
      }

      return { success: true, error: false, values: formValues }
    },
  })

  const actionButtonTitle = status === 'PUBLISHED' ? isPending || isLoading ? "Publishing..." : "Publish" : isPending || isLoading ? "Saving..." : "Save"

  //reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSuccessMessage("")
      setErrorMessage("")
      setMediaFile(null)
      setPreview(null)
      resetForm()
    }
  }, [isOpen, resetForm])

  if (isLoadingUserWithBusinesses || isLoadingBusinessData) {
    return <Loading />
  }

  return (
    <ModalBasic title="Create New Update" isOpen={isOpen} setIsOpen={() => setIsOpen(false)}>
      <div className="w-full">
        <div className="flex flex-wrap justify-center items-center">
          <div className="w-full max-w-lg mx-auto bg-white dark:bg-gray-800 px-6 pt-6 rounded-lg shadow-md">
            <form
              onSubmit={e => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)

                startTransition(() => {
                  formAction(formData)
                })
              }}
              className="mb-5"
            >
              {successMessage && <Banner02 type="success">{successMessage}</Banner02>}

              <div className="mb-4">
                <TextInput
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={handleChange}
                  placeholder="Name"
                  errors={errors}
                  type="text"
                  required
                />
              </div>
              <div className="mb-4">
                <TextInput name="tag" label="Tag" value={values.tag} onChange={handleChange} placeholder="Tag" errors={errors} type="text" />
              </div>
              <div className="mb-4">
                <TextInput
                  name="category"
                  label="Category"
                  value={values.category}
                  onChange={handleChange}
                  placeholder="Category"
                  errors={errors}
                  type="text"
                />
              </div>
              <div className="mb-4">
                <TextAreaInput
                  name="message"
                  label="Message"
                  value={values.message}
                  onChange={handleChange}
                  placeholder=""
                  errors={errors}
                  required
                />
              </div>

              <div className="mb-4 flex items-center gap-3">
                <SingleImageUpload
                  boxSize={{ height: 76, width: 146.9 }}
                  onSetFile={setMediaFile}
                  initialFile={mediaFile}
                  image={companyUpdateImageKey}
                  setPreview={setPreview}
                  className={"w-[146.9px] h-[76px] rounded-xl object-cover"}
                  noImageClassName={"w-[146.9px] h-[76px] border-dashed border-[2px] border-[#71717A] rounded-xl"}
                  imageBorder="rounded-xl"
                />

                <div>
                  <p className="text-[14px] mb-2">
                    Drag and drop or click to upload <span className="text-red-500">*</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Maximum size: 5 MB, png or jpeg</p>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{errorMessage && <p className="text-xs mt-1 text-red-500">{errorMessage}</p>}</div>
                </div>
              </div>

              <div className="mb-4 max-w-[160px]">
                <CustomSingleSelectInput
                  name="status"
                  placeholder="Select Status"
                  label="Status"
                  defaultOptionValue={status}
                  onOptionChange={option => option && setStatus(option.value.toString() as CompanyUpdateStatus)}
                  options={statusOptions}
                  disabled={isPending || isLoading || isAwsLoading}
                  fullWidth
                />
              </div>

              <div className="w-full flex justify-end gap-2">
                <ActionButton
                  type="button"
                  disabled={isPending || isLoading || isAwsLoading}
                  className="border-gray-500"
                  title="Cancel"
                  handleClick={() => {
                    resetForm()
                    setIsOpen(false)
                  }}
                />

                <ButtonLevelled
                  level="SUCCESS"
                  type="submit"
                  disabled={isPending || isLoading || isAwsLoading}
                  text={actionButtonTitle}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalBasic>
  )
}

export default CompanyUpdateModal
